// "AI ile öner" sekmesi mantığı (saf): durum makinesi, öneri ağacı, yeniden-öner semantiği, hata metni
// ve iptal edilebilir koşucu. Yanıtta maliyet/token/süre yoktur ve yüzeyde gösterilmez.

import type { KpiMetric, KpiPackageSuggestResponse } from "@mahir/shared";
import { KPI_PACKAGES_COPY, packageErrorText } from "./kpi-packages-copy";
import type { PackagePick } from "./kpi-package-draft";

export type SuggestState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "done"; res: KpiPackageSuggestResponse }
  | { kind: "error"; code: string };

export type SuggestAction =
  | { type: "start" }
  | { type: "done"; res: KpiPackageSuggestResponse }
  | { type: "fail"; code: string }
  | { type: "reset" };

export const SUGGEST_IDLE: SuggestState = { kind: "idle" };

export function suggestReducer(s: SuggestState, a: SuggestAction): SuggestState {
  switch (a.type) {
    case "start":
      return { kind: "loading" };
    case "done":
      return s.kind === "loading" ? { kind: "done", res: a.res } : s;
    case "fail":
      return s.kind === "loading" ? { kind: "error", code: a.code } : s;
    case "reset":
      return SUGGEST_IDLE;
  }
}

/** Ağaçta gösterilecek kalemler: pakette olmayan öneriler + ataları (yalnız verilen metrik listesinden). */
export function suggestTreeMetrics(
  metrics: readonly KpiMetric[],
  res: KpiPackageSuggestResponse,
): KpiMetric[] {
  const byId = new Map(metrics.map((m) => [m.id, m]));
  const inPkg = new Set(res.alreadyInPackage);
  const keep = new Set<string>();
  for (const s of res.suggestions) {
    if (inPkg.has(s.metricId) || !byId.has(s.metricId)) continue;
    let cur: KpiMetric | undefined = byId.get(s.metricId);
    while (cur && !keep.has(cur.id)) {
      keep.add(cur.id);
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
  }
  return metrics.filter((m) => keep.has(m.id));
}

/** Pakette olmayan önerilerin sayısı (özet satırı ve boş durum için). */
export function visibleSuggestions(
  res: KpiPackageSuggestResponse,
): KpiPackageSuggestResponse["suggestions"] {
  const inPkg = new Set(res.alreadyInPackage);
  return res.suggestions.filter((s) => !inPkg.has(s.metricId));
}

/**
 * Yeniden öner: yeni öneriler AI kaynaklı eklenir (gerekçe güncellenir); Seçim'de duran hiçbir kalem
 * silinmez (önceki koşumdan tutulanlar dahil); kullanıcının çıkardığı eski öneri, yeni koşum onu
 * yeniden önermedikçe geri gelmez. Elle seçilmiş kalemin kaynağı değişmez.
 */
export function applySuggestRun(
  picks: readonly PackagePick[],
  res: KpiPackageSuggestResponse,
): PackagePick[] {
  // önceki koşumun kalemleri zaten `picks` içinde (yoksa kullanıcı çıkarmıştır) — ayrıca izlenmez
  const out = new Map(picks.map((p) => [p.metricId, p]));
  for (const s of visibleSuggestions(res)) {
    const cur = out.get(s.metricId);
    if (cur?.source === "manual") continue;
    out.set(s.metricId, { metricId: s.metricId, source: "ai", reason: s.reason });
  }
  return [...out.values()];
}

/**
 * AI sekmesi ağacında işaret değişimi → Seçim. Ağaçta GÖRÜNEN kimlikler için `next` esastır:
 * işareti kalkan Seçim'den çıkar; işaretlenen, Seçim'de yoksa önerisi varsa AI kaynaklı (gerekçeyle),
 * yoksa elle eklenmiş sayılır; Seçim'de duran kalemin kaynağı değişmez. Ağaç dışındaki kimliklere
 * (Katalog sekmesinden gelenler) dokunulmaz.
 */
export function mergeSuggestSelection(
  prev: ReadonlyMap<string, PackagePick>,
  shown: ReadonlySet<string>,
  next: ReadonlySet<string>,
  reasons: ReadonlyMap<string, string>,
): Map<string, PackagePick> {
  const out = new Map<string, PackagePick>();
  for (const [id, pick] of prev) if (!shown.has(id)) out.set(id, pick);
  for (const id of next) {
    if (!shown.has(id)) continue;
    const reason = reasons.get(id);
    out.set(
      id,
      prev.get(id) ??
        (reason !== undefined
          ? { metricId: id, source: "ai", reason }
          : { metricId: id, source: "manual", reason: null }),
    );
  }
  return out;
}

export function suggestErrorText(code: string): string {
  return packageErrorText(code in KPI_PACKAGES_COPY.errors ? code : "KPI_SUGGEST_FAILED");
}

export interface SuggestRunner {
  run: (packageId: string, note: string) => void;
  /** uçuştaki isteği iptal eder; geç yanıt durumu değiştirmez */
  dispose: () => void;
}

export function createSuggestRunner(
  fetcher: (
    id: string,
    body: { note?: string },
    signal: AbortSignal,
  ) => Promise<KpiPackageSuggestResponse>,
  dispatch: (a: SuggestAction) => void,
): SuggestRunner {
  let ctl: AbortController | null = null;
  return {
    run(packageId, note) {
      ctl?.abort();
      const mine = new AbortController();
      ctl = mine;
      dispatch({ type: "start" });
      const trimmed = note.trim();
      fetcher(packageId, trimmed ? { note: trimmed } : {}, mine.signal).then(
        (res) => {
          if (!mine.signal.aborted) dispatch({ type: "done", res });
        },
        (e: unknown) => {
          if (mine.signal.aborted) return;
          const code = (e as { code?: unknown } | null)?.code;
          dispatch({ type: "fail", code: typeof code === "string" ? code : "KPI_SUGGEST_FAILED" });
        },
      );
    },
    dispose() {
      ctl?.abort();
      ctl = null;
    },
  };
}
